import { useCallback, useEffect, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { useDebouncedEffect } from 'core-components/hooks/useDebounced'
import { Input } from 'core-components/library/inputs'
import { BaseMultiSelect } from 'core-components/library/multiSelect/BaseMultiSelect'
import { ICommonMultiselect } from 'core-components/library/multiSelect/models/ICommonMultiselect'
import { useFilterSource } from 'core-components/library/selects'
import { ValidateInput } from 'core-components/library/validation'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'

export interface IMultiselectDynamicProps<T = IForm, R = string | number>
  extends ICommonMultiselect<T, R> {
  /**
   * Fields to be searched
   */
  fieldsToSearch?: string[]
  maxListPageSize?: number
}

export function MultiSelectDynamic<T = IForm, R = string | number>({
  value,
  source,
  label,
  placeholder,
  caption,
  onChange,
  disabled,
  useRowCheckbox,
  onCheck,
  onDataLoad,
  checkedValues,
  margin,
  maxListPageSize,
  classes,
  limit,
  renderFunction,
  noFilterOnSelect,
  useDrag,
  fieldsToSearch,
  hierarchyField,
  expanded,
  itemTemplate,
  closeOnSelect = true,
  InputComponent = Input,
  ValidateInputComponent = ValidateInput,
  prepareInputConfig,
  itemClickCallback,
  filter,
  _popupVisible,
}: IMultiselectDynamicProps<T, R>) {
  const [inputted, setInputted] = useState<string>('')
  const [currentValue, setCurrentValue] = useState<any[]>(value)
  const inputRef = useRef<HTMLInputElement>(null)

  const { staticSource, isDataLoading, updateSourceFilter, data } = useFilterSource<T, R>({
    source,
    caption,
    filter,
    maxListPageSize,
    onDataLoad,
  })

  const getBaseFilter = useCallback(() => {
    let filters: IFilter[] = []

    if (!useRowCheckbox && !noFilterOnSelect) {
      filters = value.map((el) => {
        return {
          field: source.keyField,
          operator: Operator.NOTEQUAL,
          value: el,
        }
      })
    }

    return filters
  }, [useRowCheckbox, noFilterOnSelect, value, source.keyField])

  const updateFilter = useCallback(() => {
    const baseFilter = getBaseFilter()
    updateSourceFilter(baseFilter)
  }, [getBaseFilter, updateSourceFilter])

  useEffect(() => {
    setCurrentValue(value.map((el) => ({ [caption]: el, [source.keyField]: el })))

    updateFilter()
  }, [maxListPageSize, value, caption, updateFilter, source.keyField])

  useDebouncedEffect(
    () => {
      const baseFilter = getBaseFilter()

      const searchFilters = fieldsToSearch
        ? fieldsToSearch.map((field) => {
            return {
              field: field,
              operator: Operator.CONTAINS,
              value: inputted,
            }
          })
        : [
            {
              field: caption,
              operator: Operator.CONTAINS,
              value: inputted,
            },
          ]
      if (!searchFilters.length || !inputted) {
        updateFilter()
      } else {
        const newFilter: IDataProviderFilter = {
          logic: Logic.AND,
          filters: [
            {
              logic: Logic.OR,
              filters: [...searchFilters],
            },
          ],
        }

        if (baseFilter.length) {
          newFilter.filters.push({
            filters: baseFilter,
            logic: Logic.AND,
          })
        }

        updateSourceFilter(newFilter.filters)
      }
    },
    400,
    [inputted, getBaseFilter, updateSourceFilter, updateFilter, fieldsToSearch, filter]
  )

  return (
    <BaseMultiSelect
      inputted={inputted}
      setInputted={setInputted}
      items={data}
      currentValue={currentValue}
      setCurrentValue={setCurrentValue}
      inputRef={inputRef}
      value={value}
      itemClickCallback={itemClickCallback}
      itemTemplate={itemTemplate}
      source={staticSource}
      label={label}
      placeholder={placeholder}
      caption={caption}
      onChange={onChange}
      disabled={disabled}
      useRowCheckbox={useRowCheckbox}
      onCheck={onCheck}
      checkedValues={checkedValues}
      checkFn={null}
      margin={margin}
      maxListPageSize={maxListPageSize}
      classes={classes}
      limit={limit}
      renderFunction={renderFunction}
      useDrag={useDrag}
      noFilterOnSelect={noFilterOnSelect}
      prepareInputConfig={prepareInputConfig}
      hierarchyField={hierarchyField}
      expanded={expanded}
      _popupVisible={_popupVisible}
      InputComponent={InputComponent}
      ValidateInputComponent={ValidateInputComponent}
      closeOnSelect={closeOnSelect}
      wip={isDataLoading}
    />
  )
}
